import { useTranslation } from "react-i18next";

import { MobileFiltersBottomSheet } from "../../Common/controls/MobileFiltersBottomSheet";
import { useClientAccountReportContext } from "../providers/AccountReportProvider";

type MobileFiltersV2Props = {
  handleSearchInput: (value: string) => void;
  canViewPII: boolean;
  redactedFields?: string[];
  /** Gated by the part-3795-materialized-view-hoid flag. */
  showRebateProgramFilter?: boolean;
};

// Stable reference for the "nothing selected yet" fallback below. A fresh
// `[]` literal evaluated on every render would defeat MobileFiltersBottomSheet's
// reference-based effect and loop it forever once anything else re-renders.
const EMPTY_SELECTION: string[] = [];

export const MobileFiltersV2 = ({
  handleSearchInput,
  canViewPII,
  redactedFields,
  showRebateProgramFilter,
}: MobileFiltersV2Props) => {
  const { t } = useTranslation();
  const {
    state: {
      searchQuery,
      selected: {
        rebateAccountIDs: selectedRebateAccountIDs,
        rebateProgramIDs: selectedRebateProgramIDs,
      },
    },
    options: { rebateAccountIDs, rebateProgramIDs },
    actions,
  } = useClientAccountReportContext();

  // No date filter on Account Report -- unlike Trading Report, there's no
  // always-active base count. Matches the desktop Filter modal's
  // filterCount.
  const filterCount =
    ((selectedRebateAccountIDs as string[] | null)?.length ? 1 : 0) +
    (showRebateProgramFilter &&
    (selectedRebateProgramIDs as string[] | null)?.length
      ? 1
      : 0);

  const handleApply = (params: {
    rebateAccountIDs: string[];
    rebateProgramIDs: string[];
  }) => {
    actions.setSelected("rebateAccountIDs", params.rebateAccountIDs);
    actions.setSelected("rebateProgramIDs", params.rebateProgramIDs);
  };

  // Reset is draft-only -- MobileFiltersBottomSheet already resets its own
  // local draft (rebate account/program selections) when its Reset button
  // is clicked. Nothing here should touch the report context; only Apply
  // may call an API.

  return (
    <div className="sticky top-[64px] z-10 mt-6 bg-surface-primary pb-4 pt-4">
      <MobileFiltersBottomSheet
        title={t("clientsPage_tradingReport_filters_filterButton")}
        emptyMeansAll
        handleSearchInput={handleSearchInput}
        canViewPII={canViewPII}
        redactedFields={redactedFields}
        searchQuery={searchQuery}
        rebateAccountOptions={rebateAccountIDs}
        selectedRebateAccountIDs={
          (selectedRebateAccountIDs as string[] | null) ?? EMPTY_SELECTION
        }
        showRebateProgramFilter={showRebateProgramFilter}
        rebateProgramOptions={rebateProgramIDs}
        selectedRebateProgramIDs={
          (selectedRebateProgramIDs as string[] | null) ?? EMPTY_SELECTION
        }
        showDateFilter={false}
        containerClassName="flex flex-row items-center gap-2"
        searchInputClassName="!outline-0 hover:!outline-0 focus:!outline-2 focus:!outline-stroke-focused"
        filterCount={filterCount}
        onApply={handleApply}
      />
    </div>
  );
};
